<template>
    <div class="advanced-home-page">
        <VJoyWrapper v-if="!submitting" direction="column" class="advanced-home" row-gap="9" align="stretch">
            <VJoyWrapper direction="column" row-gap="4" no-margin>
                <div class="advanced-home__header-icon">
                    <VJoyIcon style="vertical-align: bottom" class="ai-color-gradient" name="sparkles" size="small" />
                </div>
                <h1>
                    {{ $t('search.advanced.home-v2.home.title.v2') }}
                </h1>
                <div class="advanced-home__description">
                    <ol>
                        <li>{{ $t('search.advanced.home-v2.home.description.list.1') }}</li>
                        <li>{{ $t('search.advanced.home-v2.home.description.list.2') }}</li>
                        <li>{{ $t('search.advanced.home-v2.home.description.list.3.v2') }}</li>
                    </ol>
                </div>
            </VJoyWrapper>
            <CompanyMemberDropdown
                v-if="clientCompanyId && isAdmin"
                v-model="member"
                :company-id="clientCompanyId"
                class="advanced-home__company-member-dropdown"
            />
            <AdvancedSearch
                :create-advanced-search-from-file="createAdvancedSearchFromFile"
                :create-advanced-search-from-prompt="createAdvancedSearchFromPrompt"
                :submitting="advancedSearchSubmittingAndRedirecting"
                :context="member"
                :is-on-behalf="!!clientCompanyId && isAdmin"
            />
        </VJoyWrapper>
        <AdvancedSearchCreationLoader v-else />
    </div>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useRoute} from '@typed-router';
    import {useHasEnoughPriviledge, onMounted, definePageMeta} from '#imports';
    import {useEventBus} from '@malt/utils';
    import {VJoyWrapper, VJoyIcon} from '@maltjoy/core-vue';
    import {type ClientUserResource} from '@api';
    import {
        useAdvancedSearchPromptApi,
        useAdvancedSearchFileUploadApi,
    } from '~/features/advanced-search/composables/job-description/useAdvancedSearchApi';
    import CompanyMemberDropdown from '~/features/advanced-search/index/company-member-dropdown/CompanyMemberDropdown.vue';
    import AdvancedSearch from '~/features/advanced-search/job-description/AdvancedSearch.vue';
    import AdvancedSearchCreationLoader from '~/features/advanced-search/job-description/AdvancedSearchCreationLoader.vue';
    import {eventBus as legacyEventBus} from '~/features/advanced-search/job-description/events';
    import {useAdvancedSearchTracker} from '~/features/advanced-search/tracking/tracker';

    definePageMeta({
        layout: 'advanced',
    });

    const {trackFunnelStepViewed} = useAdvancedSearchTracker();

    const route = useRoute('s-advanced');

    const {createAdvancedSearchFromPrompt} = useAdvancedSearchPromptApi();
    const {createAdvancedSearchFromFile} = useAdvancedSearchFileUploadApi();

    const advancedSearchSubmittingAndRedirecting = ref(false);

    const submitting = computed(() => advancedSearchSubmittingAndRedirecting.value);

    const isAdmin = await useHasEnoughPriviledge(['ROLE_ADMIN']);

    const clientCompanyId: string | undefined = route.query.clientCompanyId?.toString();

    const member = ref<ClientUserResource | undefined>();

    onMounted(() => {
        trackFunnelStepViewed();
    });

    useEventBus(legacyEventBus, 'submittingAndRedirecting', () => {
        advancedSearchSubmittingAndRedirecting.value = true;
    });

    useEventBus(legacyEventBus, 'redirecting:abort', () => {
        advancedSearchSubmittingAndRedirecting.value = false;
    });
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint' as joy;

    .ai-color-gradient {
        background: linear-gradient(to left, #fc5757d9, #9035a2);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .advanced-home {
        margin: auto;
        max-width: 672px;

        @include joy.screen_sm(max) {
            margin: 0 var(--joy-core-spacing-4);
        }

        &-page {
            padding-top: var(--joy-core-spacing-10);

            @include joy.screen_sm(max) {
                padding-top: var(--joy-core-spacing-12);
            }
        }

        h1 {
            text-align: center;
            font-family: var(--joy-font-family-title);
            font-size: var(--joy-font-size-secondary-200) !important;
            margin-bottom: 0;
        }

        &__description {
            font-size: var(--joy-font-size-primary-400);
            align-self: center;

            ol {
                margin: 0;
                padding: 0;
                text-align: center;
                list-style-position: inside;
            }
        }

        &__header-icon {
            display: flex;
            justify-content: center;
        }

        &__link {
            align-self: flex-start;
        }

        .search {
            border-width: var(--joy-form-border-width);
            border-style: solid;
            border-color: var(--joy-color-neutral-30);
            border-radius: var(--joy-form-field-radius-medium);
            padding: var(--joy-core-spacing-useAmplitudeSessionReplay1);

            .search--input {
                border: 0;
            }

            .search--submit {
                flex-grow: 0;
            }
        }

        .pdf--submit {
            flex-grow: 0;
        }
    }
    :root {
        --spinner-default-color: var(--joy-color-primary-50);
        --spinner-default-bg-color: var(--joy-color-primary-10);
        --spinner-size: 60px;
    }
    .nuxt-application-loader {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
    }

    .nuxt-application-spinner {
        position: absolute;
        margin: auto;
        font-size: 10px;
        text-indent: -9999em;
        transform: translateZ(0);
        -webkit-animation: load8 1.1s infinite linear;
        animation: load8 1.1s infinite linear;
        border-left: 3px solid var(--spinner-default-color);
        border-radius: var(--joy-core-radius-6);
        width: var(--spinner-size);
        height: var(--spinner-size);
        box-sizing: border-box;
        border-top: 3px solid var(--spinner-default-bg-color);
        border-right: 3px solid var(--spinner-default-bg-color);
        border-bottom: 3px solid var(--spinner-default-bg-color);
    }

    .nuxt-application-spinner:after {
        content: '';
        display: block;
        border-radius: var(--joy-core-radius-6);
        width: var(--spinner-size);
        height: var(--spinner-size);
    }

    @-webkit-keyframes load8 {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }

    @keyframes load8 {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
</style>
